import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {setMessage} from './message';

import profileQuotationService from '../services/profile-quotation.service';


export const fetchProfileQuotationList = createAsyncThunk(
    'profileQuotation/fetchProfileQuotationList',
    async ({page, pagesize, search, groupId, ordering}, thunkAPI) => {
        try {
            const response = await profileQuotationService.getProfileQuotationList(page, pagesize, search, groupId, ordering);
            return response.data;
        } catch (error) {
            const message =
                (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const fetchArchivedProfileQuotationList = createAsyncThunk(
    'profileQuotation/fetchArchivedProfileQuotationList',
    async ({page, pagesize, search, ordering}, thunkAPI) => {
        try {
            const response = await profileQuotationService.getArchiveProfileQuotationList(page, pagesize, search, ordering);
            return response.data;
        } catch (error) {
            const message =
                (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);
export const fetchProfileQuotationById = createAsyncThunk(
    'profileQuotation/fetchProfileQuotationById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await profileQuotationService.getProfileQuotationById(id);
            return response.data; // Return the profile data
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);
export const createProfileQuotation = createAsyncThunk(
    'profileQuotation/createProfileQuotation',
    async ({ values }, thunkAPI) => {
        try {
            const response = await profileQuotationService.createProfileQuotation(values);
            return response.data;
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);
export const updateProfileQuotation = createAsyncThunk(
    'profileQuotation/updateProfileQuotation',
    async ({ id, values}, thunkAPI) => {
        try {
            const response = await profileQuotationService.editProfileQuotation(id, values);
            return response.data; // Return the updated die group data
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const deleteProfileQuotation = createAsyncThunk(
    'profileQuotation/deleteProfileQuotation',
    async (id, thunkAPI) => {
        try {
            await profileQuotationService.deleteProfileQuotation(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);


export const unArchiveProfileQuotation = createAsyncThunk(
    'profileQuotation/unArchiveProfileQuotation',
    async (id, thunkAPI) => {
        try {
            await profileQuotationService.unArchiveProfileQuotation(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

const initialState = {
    data: [], // List
    list: [],
    totalPages: 0,
    currentPage: 1,
    totalRecord: 0,
    status: 'idle',
    error: null,
    loading: false,
    hasMore: true,
};


const profileQuotationSlice = createSlice({
    name: 'profileQuotation',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        resetProfileQuotationData: (state) => {
            state.data = []; // This will clear the list
        },
        resetProfileQuotationList: (state) => {
            state.list = []; // This will clear the list
        },
        resetCurrentProfileQuotationPage: (state) => {
            state.currentPage = 1;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchProfileQuotationList.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchProfileQuotationList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentPage;
                state.data = action.payload.results.data || [];  // Update to match your API response
                state.totalRecord = action.payload.count || 0; // Handle count (total records)
                state.totalPages = action.payload.totalPages || 0; // Handle totalPages
                state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchProfileQuotationList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
            });
        builder
            .addCase(fetchArchivedProfileQuotationList.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchArchivedProfileQuotationList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentPage;
                state.data = action.payload.results.data || [];  // Update to match your API response
                state.totalRecord = action.payload.count || 0; // Handle count (total records)
                state.totalPages = action.payload.totalPages || 0; // Handle totalPages
                state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchArchivedProfileQuotationList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
            });
        builder
            .addCase(fetchProfileQuotationById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProfileQuotationById.fulfilled, (state, action) => {
                state.loading = false;
                state.activeProfileRecord = action.payload;
            })
            .addCase(fetchProfileQuotationById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(createProfileQuotation.pending, state => {
                state.status = 'loading';
            })
            .addCase(createProfileQuotation.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data.push(action.payload); // Add the newly created group to the list
            })
            .addCase(createProfileQuotation.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(updateProfileQuotation.pending, state => {
                state.status = 'loading';
            })
            .addCase(updateProfileQuotation.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const updateDieProfile = action.payload;
                const index = state.data.findIndex(group => group.id === updateDieProfile.id); // Find the index of the updated group
                if (index !== -1) {
                    state.data[index] = updateDieProfile; // Replace the old group with the updated one
                }
            })
            .addCase(updateProfileQuotation.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
        builder
            .addCase(deleteProfileQuotation.pending, state => {
                state.status = 'loading';
            })
            .addCase(deleteProfileQuotation.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
                state.list = state.list.filter(group => group.id !== action.payload); // Remove the deleted group from list
            })
            .addCase(deleteProfileQuotation.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(unArchiveProfileQuotation.pending, state => {
                state.status = 'loading';
            })
            .addCase(unArchiveProfileQuotation.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
            })
            .addCase(unArchiveProfileQuotation.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { resetProfileQuotationData, resetProfileQuotationList, resetCurrentProfileQuotationPage } = profileQuotationSlice.actions;
export default profileQuotationSlice.reducer;
